import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowLeft, Send, Check, Building2, Users, Link2, FileText, Sparkles, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import Navbar from '../sections/Navbar';
import Footer from '../sections/Footer';
import { submitToGoogleSheets } from '../utils/googleSheetsIntegration';

gsap.registerPlugin(ScrollTrigger);

const packageInfo: Record<string, { name: string; price: string; description: string }> = {
  start: {
    name: 'Start',
    price: '4 900 Kč',
    description: 'Jednorázový audit pro menší weby (do 50 produktů)',
  },
  rust: {
    name: 'Růst',
    price: '9 900 Kč/měsíc',
    description: 'Měsíční spolupráce pro e-shopy (50-500 produktů)',
  },
  premium: {
    name: 'Premium',
    price: '24 900 Kč/měsíc',
    description: 'Měsíční spolupráce pro velké e-shopy (500+ produktů)',
  },
};

const AuditPage = () => {
  const [searchParams] = useSearchParams();
  const selectedPackage = searchParams.get('package') || '';
  
  const [formData, setFormData] = useState({
    companyName: '',
    companySize: '',
    websiteUrl: '',
    description: '',
    email: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const ctx = gsap.context(() => {
      gsap.from(formRef.current, {
        opacity: 0,
        y: 30,
        duration: 0.8,
        ease: 'power2.out',
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Odeslání do Google Sheets
    await submitToGoogleSheets({
      companyName: formData.companyName,
      companySize: formData.companySize,
      websiteUrl: formData.websiteUrl,
      description: formData.description,
      email: formData.email,
      formType: selectedPackage ? (selectedPackage as 'start' | 'rust' | 'premium') : 'audit',
      packageName: packageInfo[selectedPackage]?.name || 'AI Audit',
    });

    // Zobrazení success message
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset formuláře po 3 sekundách
    setTimeout(() => {
      setFormData({
        companyName: '',
        companySize: '',
        websiteUrl: '',
        description: '',
        email: '',
      });
    }, 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const selectedPackageInfo = selectedPackage ? packageInfo[selectedPackage] : null;

  return (
    <div ref={pageRef} className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50">
      <Navbar />
      
      <main className="relative z-10 pt-24 pb-16">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <Link to="/">
              <Button
                variant="ghost"
                className="mb-6 text-slate-500 hover:text-slate-700 -ml-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zpět na hlavní stránku
              </Button>
            </Link>

            <div ref={formRef}>
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-4">
                  {selectedPackageInfo ? (
                    <>
                      <Package className="w-4 h-4 text-[#0ea5e9]" />
                      <span className="text-sm text-slate-600">{selectedPackageInfo.name}</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 text-[#0ea5e9]" />
                      <span className="text-sm text-slate-600">Zdarma</span>
                    </>
                  )}
                </div>
                
                <h1 className="text-3xl sm:text-4xl font-bold mb-4 text-slate-800">
                  {selectedPackageInfo ? (
                    <>Vybrali jste <span className="text-gradient">{selectedPackageInfo.name}</span></>
                  ) : (
                    <>AI Visibility <span className="text-gradient">Audit</span></>
                  )}
                </h1>
                
                <p className="text-slate-500">
                  {selectedPackageInfo ? (
                    <>
                      {selectedPackageInfo.description}
                      <br />
                      <span className="font-semibold text-slate-700">{selectedPackageInfo.price}</span>
                    </>
                  ) : (
                    'Zjistěte, jak je váš web viditelný v AI vyhledávačích. Do 48 hodin dostanete detailní analýzu a doporučení.'
                  )}
                </p>
              </div>

              {/* Form */}
              <div className="glass-card rounded-2xl p-6 md:p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-emerald-500" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">Děkujeme!</h3>
                    <p className="text-slate-500 mb-4">
                      Vaši žádost jsme přijali. Do 48 hodin vás budeme kontaktovat.
                    </p>
                    <Link to="/">
                      <Button variant="outline" className="border-slate-300">
                        Zpět na hlavní stránku
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Název firmy */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <Building2 className="w-4 h-4 inline mr-1" />
                        Název firmy
                      </label>
                      <Input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder="Např. Moje Firma s.r.o."
                        required
                        className="bg-white/50 border-slate-200 text-slate-800 placeholder:text-slate-400"
                      />
                    </div>

                    {/* Velikost firmy */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <Users className="w-4 h-4 inline mr-1" />
                        Velikost firmy
                      </label>
                      <select
                        name="companySize"
                        value={formData.companySize}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 rounded-md bg-white/50 border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent"
                      >
                        <option value="">Vyberte velikost</option>
                        <option value="1-10">1-10 zaměstnanců</option>
                        <option value="11-50">11-50 zaměstnanců</option>
                        <option value="51-200">51-200 zaměstnanců</option>
                        <option value="200+">200+ zaměstnanců</option>
                      </select>
                    </div>

                    {/* Odkaz na web */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <Link2 className="w-4 h-4 inline mr-1" />
                        Odkaz na web
                      </label>
                      <Input
                        type="url"
                        name="websiteUrl"
                        value={formData.websiteUrl}
                        onChange={handleChange}
                        placeholder="https://vasefirma.cz"
                        required
                        className="bg-white/50 border-slate-200 text-slate-800 placeholder:text-slate-400"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Váš e-mail
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="vas@email.cz"
                        required
                        className="bg-white/50 border-slate-200 text-slate-800 placeholder:text-slate-400"
                      />
                    </div>

                    {/* Popis */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <FileText className="w-4 h-4 inline mr-1" />
                        Stručný popis, jak je web spravován
                      </label>
                      <Textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Popište, kdo spravuje váš web, jak často aktualizujete obsah, jaké máte cíle..."
                        rows={4}
                        className="bg-white/50 border-slate-200 text-slate-800 placeholder:text-slate-400 resize-none"
                      />
                    </div>

                    {/* Submit */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-6 bg-gradient-to-r from-[#0ea5e9] to-[#00d4aa] text-white font-semibold hover:opacity-90 transition-all"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Odesílání...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-4 h-4" />
                          {selectedPackageInfo ? 'Odeslat žádost' : 'Odeslat žádost o audit'}
                        </span>
                      )}
                    </Button>

                    <p className="text-center text-xs text-slate-400">
                      Odesláním souhlasíte se zpracováním osobních údajů. 
                      Vaše data budou použita pouze pro účely komunikace.
                    </p>
                  </form>
                )}
              </div>

              {/* Info */}
              {!selectedPackageInfo && !isSubmitted && (
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="glass-card rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-gradient mb-1">48h</div>
                    <div className="text-sm text-slate-500">Doba zpracování</div>
                  </div>
                  <div className="glass-card rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-gradient mb-1">Zdarma</div>
                    <div className="text-sm text-slate-500">Bez závazků</div>
                  </div>
                  <div className="glass-card rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-gradient mb-1">100%</div>
                    <div className="text-sm text-slate-500">Detailní analýza</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AuditPage;
